import { Modal, Select, Spin, Table, Typography } from "antd";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Papaparse from "papaparse";
import classNames from "classnames";
import { getLoseExpenseDict } from "service/economics/upload";
import classes from "./loe-expense-data-import.module.scss";

/**
 * @type {React.FC<{
 *  data: Array<string[]>
 * }>}
 */
const DataTable = forwardRef(({ inputData }, ref) => {
  /**
   * @type {[Record<number, string>, React.Dispatch<Record<number, string>>]}
   */
  const [tblColumns, setTblColumns] = useState([]);
  const [tblData, setTblData] = useState([]);
  const [columnsMap, setColumnMap] = useState({});
  const [columnOptions, setColumnOptions] = useState([]);
  // const headerSelectRefs = useRef([]);

  useImperativeHandle(ref, () => ({
    getImportData: () => {
      let keys = Object.keys(columnsMap);
      keys.sort();

      return {
        dataStructure: keys.map((key) => columnsMap[key]),
        data: inputData.records,
      };
    },
  }));

  const titleCase = (s) =>
    s
      .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()) // Initial char (after -/_)
      .replace(/[-_]+(.)/g, (_, c) => " " + c.toUpperCase()); // First char after each -/_

  const getDefaultColumnValue = useCallback(
    (header, index) => {
      let headerValue = header.toLowerCase().replace(/\s+/g, "_");
      for (let i = 0; i < columnOptions.length; i++) {
        if (
          columnOptions[i].value === headerValue ||
          columnOptions[i].value.includes(headerValue)
        ) {
          return columnOptions[i].value;
        }
      }

      let idx = Math.min(index, columnOptions.length - 1);
      return columnOptions[idx].value; // default to the first column
    },
    [columnOptions]
  );

  const onSelectColumnName = useCallback((index, colName) => {
    setColumnMap((prev) => ({
      ...prev,
      [index]: colName,
    }));
    // headerSelectRefs.current[index].value = colName;
  }, []);

  useEffect(() => {
    getLoseExpenseDict(
      (res) => {
        const colOptions = res.map((item) => {
          return {
            value: item,
            label: titleCase(item),
          };
        });
        setColumnOptions(colOptions);
      },
      (error) => {
        console.error(error);
      }
    );
  }, []);

  useEffect(() => {
    if (
      !columnOptions ||
      !columnOptions.length ||
      !inputData ||
      !inputData.headers ||
      !inputData.headers.length
    ) {
      return;
    }
    const inputHeaders = inputData.headers;
    // headerSelectRefs.current.slice(0, inputHeaders.length);

    let colsMap = {};
    let cols = inputHeaders.map((header, index) => {
      let columnValue = getDefaultColumnValue(header, index);
      console.log("init column header ==>", header, columnValue);
      colsMap[index] = columnValue;
      return {
        title: (props) => (
          <div className="vstack gap-1">
            <Typography.Text>{header}</Typography.Text>
            <Select
              // ref={(el) => (headerSelectRefs.current[index] = el)}
              className={classes.columnSelector}
              dropdownMatchSelectWidth={false}
              defaultValue={columnValue}
              onChange={(val) => onSelectColumnName(index, val)}
              options={columnOptions}
            />
          </div>
        ),
        dataIndex: index,
      };
    });
    setColumnMap(colsMap);
    setTblColumns(cols);

    if (inputData.records && inputData.records.length > 0) {
      let tblData = inputData.records.map((record, index) => ({
        ...record,
        index,
      }));
      setTblData(tblData);
    }
  }, [columnOptions, inputData, onSelectColumnName]);

  return (
    <Table
      bordered
      columns={tblColumns}
      rowKey="index"
      dataSource={tblData}
      pagination={false}
      scroll={{
        y: 400,
      }}
      style={{ height: "100%" }}
    />
  );
});

DataTable.displayName = "LoseExpenseImportDataTable";

/**
 *
 * @param {{
 * file: File
 * }} props
 * @returns
 */
const DataVisualizer = forwardRef(({ file }, ref) => {
  /**
   * @type {[Papaparse.ParseResult, React.Dispatch<Papaparse.ParseResult>]}
   */
  const [result, setResult] = useState(null);
  const [inputData, setInputData] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const transformData = useCallback((data) => {
    const headers = Object.keys(data[0]);
    const records = data.map((record) => {
      return headers.map((header) => record[header]);
    });
    return {
      headers: headers,
      records: records,
    };
  }, []);

  const processFile = useCallback((_file) => {
    setResult(null);
    setLoading(true);
    Papaparse.parse(_file, {
      skipEmptyLines: true,
      // delimiter: "\t", // auto-detect, Uncomment this line if the file is tab-separated
      header: true, // return object instead of array
      complete(result) {
        setResult(result);
        if (!result.errors.length) {
          const data = transformData(result.data);
          setInputData(data);
        }
        setLoading(false);
      },
    });
  }, []);

  useEffect(() => {
    processFile(file);
  }, [file]);

  return (
    <div className={classNames("vstack", classes.dataVisualizer)}>
      {isLoading ? (
        <Spin size="large" />
      ) : result ? (
        result.errors.length ? (
          <ul>
            {result.errors.map((err) => (
              <li key={err.code} className="text-danger">
                <Typography.Text className="text-danger">
                  Row {err.row + 1}: {err.message}
                </Typography.Text>
              </li>
            ))}
          </ul>
        ) : (
          <DataTable ref={ref} inputData={inputData} />
        )
      ) : null}
    </div>
  );
});

DataVisualizer.displayName = "LoseExpenseDataImportVisualizer";

/**
 *
 * @param {import('antd').ModalProps & {
 * isImporting: boolean
 * file: File | null
 * onClose: () => void
 * onImport: (data: Array<Record<string, string>>) => void
 * }} props
 * @returns
 */
const LOERecordsImportModal = ({
  isImporting,
  file,
  onClose,
  onImport,
  ...modalProps
}) => {
  const dataVisualizer = useRef(null);

  const onSave = useCallback(() => {
    const data = dataVisualizer.current?.getImportData();
    onImport?.(data);
  }, [onImport]);

  return (
    <Modal
      title="Import Data"
      okText="Save"
      width={'80%'}
      /*width={{
          xs: '90%',
          sm: '80%',
          md: '70%',
          lg: '60%',
          xl: '60%',
          xxl: '60%',
        }}*/
      centered
      //bodyStyle={{height: }}
      //style={{ top: 20 }}
      style={{ height: "80vh" }}
      //style={{ height: 220 }}
      {...modalProps}
      onCancel={onClose}
      onOk={onSave}
      okButtonProps={{
        loading: isImporting,
      }}
      cancelButtonProps={{
        disabled: isImporting,
      }}
    >
      {file && modalProps.open ? (
        <DataVisualizer ref={dataVisualizer} file={file} />
      ) : null}
    </Modal>
  );
};

export default LOERecordsImportModal;
